import { DomTools } from "../tools";
import { CustomElementClassConfig } from "./customElement.types";
import { WebComponent } from "./webComponent";

/**
 * Get alert message for css classes added to customElement div from @decorator config
 */
export function getElementCssClassesAlertInStyle(classes: string[]): string {
    return `/** ! Classes: ${classes.toString()} 
    are added from elementCssClasses webComponent property ! */`;
}

/**
 * This function is called by @CustomElement decorator to determine if component should be attached to a shadow DOM
 * @param wc the web component
 * @param classConfig configuration that was prepared from @CustomElement decorator options on the component class
 * @returns undefined if using lightDOM attachShadow function parameters otherwise
 */
export function attachTemplateAndStyle(wc: WebComponent, classConfig: CustomElementClassConfig | undefined): void {

    if (!classConfig) {
        // this web component is not decorated with @CustomElement
        return;
    }

    // add elementCssClasses to component div selector classlist
    const classesToAdd = [];
    const elementCssClasses = wc.customElementOptions?.elementCssClasses ?? classConfig.config.elementCssClasses;
    if (elementCssClasses) {
        classesToAdd.push(...elementCssClasses);
    }

    // Add tagName to webComponent classes
    classesToAdd.push(classConfig.config.name);
    wc.classList.add(...classesToAdd);

    // create a clone of the template and attach it to the component
    const attachShadowParams = getAttachShadowParams(wc, classConfig);
    const clone = classConfig.template?.content.cloneNode(true);
    if (attachShadowParams) {
        // component children are attached to a shadow DOM

        const shadowRoot = wc.attachShadow(attachShadowParams);
        // append style
        const style = getStyleForClassAndItsAncestors(classConfig);
        if (style) {
            shadowRoot.appendChild(DomTools.createStyleElement(style));
        }
        // append template clone
        if (clone != null) {
            shadowRoot.appendChild(clone);
        }
    } else {
        // component children are inserted in "light DOM"

        // append style
        appendDomStyleForClassAndItsAncestors(classConfig);

        if (clone != null) {
            // create a slot that acts as our component 'root' and will be attached to the component on connectedCallback (when added to the DOM)
            const slot = document.createElement("slot");
            slot.appendChild(clone);
            wc.lightDomRoot = slot;
        } else {
            wc.lightDomRoot = wc;
        }

    }
}

/**
 * @returns the concatenated styles or undefined if none have a style
 */
function getStyleForClassAndItsAncestors(classConfig: CustomElementClassConfig): string | undefined {
    const parentStyle = classConfig.parentConfig ? getStyleForClassAndItsAncestors(classConfig.parentConfig) : undefined;
    if (parentStyle == null) {
        return classConfig.style;
    }
    if (classConfig.style == null) {
        return parentStyle;
    }
    // both not null
    return parentStyle + "\n" + classConfig.style;
}

/**
 * append css style to DOM for provided class config and its ancestors
 * @param classConfig 
 */
function appendDomStyleForClassAndItsAncestors(classConfig: CustomElementClassConfig): void {
    if (classConfig.parentConfig) {
        appendDomStyleForClassAndItsAncestors(classConfig.parentConfig);
    }
    if (classConfig.style) {
        const cssUid = "web-component-" + classConfig.config.name;
        if (!DomTools.wasCssStringAlreadyAppend(cssUid)) {
            const style = "/*" + classConfig.config.name + "*/\n" + classConfig.style;
            DomTools.appendCssString(style, cssUid);
        }
    }
}

/**
 * This function is called by @CustomElement decorator to determine if component should be attached to a shadow DOM
 * @param wc the web component to attach (or not) to shadow DOM
 * @param classConfig configuration that was prepared from @CustomElement decorator options on the component class
 * @returns undefined if using lightDOM attachShadow function parameters otherwise
 */
function getAttachShadowParams(wc: WebComponent, classConfig: CustomElementClassConfig): ShadowRootInit | undefined {

    // get attachShadow params from config
    let attachShadowParams = classConfig.config.attachShadow;

    // override config with instance parameters
    if (wc.customElementOptions) {
        if (wc.customElementOptions.attachShadow) {
            attachShadowParams = wc.customElementOptions.attachShadow;// override attachShadow for this instance
        } else if (wc.customElementOptions.attachShadow === false) {
            attachShadowParams = undefined;// override attachShadow for this instance
        }
    }

    // override id attribute doNotAttachShadow is set to true
    // TODO document attributes
    if (attachShadowParams && wc.getAttribute("doNotAttachShadow") === "true") {
        attachShadowParams = undefined;
    }
    return attachShadowParams;
}
